// src/components/modals/FunnelTemplateModal.jsx
import React, { useMemo, useState } from "react";
import { Modal, Button, Badge } from "flowbite-react";

/** Tipagem de Template:
 * {
 *   id: string,
 *   name: string,
 *   description?: string,
 *   stages: string[],
 *   closingStageIndex?: number,
 *   color?: string // tailwind gradient "from-... to-..."
 * }
 */

const DEFAULT_TEMPLATES = [
  {
    id: "simple-sales",
    name: "Vendas (simples)",
    color: "from-rose-500 to-amber-500",
    description: "Fluxo direto para times comerciais enxutos.",
    stages: ["Prospecção", "Contato", "Proposta", "Negociação", "Fechamento"],
    closingStageIndex: 4,
  },
  {
    id: "sdr",
    name: "SDR → Closer",
    color: "from-indigo-500 to-cyan-500",
    description: "Qualificação por SDR antes de passar para o closer.",
    stages: [
      "Lead novo",
      "Qualificação SDR",
      "Reunião agendada",
      "Proposta enviada",
      "Fechamento",
    ],
    closingStageIndex: 4,
  },
  {
    id: "suporte",
    name: "Suporte",
    color: "from-emerald-500 to-lime-500",
    description: "Do recebimento até a resolução e follow-up.",
    stages: ["Novo", "Em análise", "Em atendimento", "Resolvido", "Follow-up"],
    closingStageIndex: 3,
  },
  {
    id: "onboarding",
    name: "Onboarding",
    color: "from-violet-500 to-fuchsia-500",
    description: "Implantação em etapas para novos clientes.",
    stages: [
      "Kickoff",
      "Setup técnico",
      "Treinamento",
      "Go-live",
      "Sucesso do cliente",
    ],
    closingStageIndex: 4,
  },
];

const VIS_MAP = {
  PRIVATE: { label: "Somente eu", badge: { color: "gray" } },
  TEAM: { label: "Time", badge: { color: "purple" } },
  PUBLIC: { label: "Público", badge: { color: "indigo" } },
};

export default function FunnelTemplateModal({
  show,
  onClose,
  onCreateFromTemplate, // (payloadPronto) => Promise<funnelCreated>
  onCreateCustom, // <<< NOVO: abre o modal de criação personalizada
  templates = DEFAULT_TEMPLATES,
  title = "Escolher template de funil",
  subtitle = "Selecione um modelo para começar mais rápido. Você poderá editar tudo depois.",

  // controle de visibilidade “fixo”
  defaultVisibility = "PUBLIC", // "PRIVATE" | "TEAM" | "PUBLIC"
  defaultTeamId = null,
  defaultTeamName = "",
  ownerUserId = null, // usado quando PRIVATE
}) {
  const [creatingId, setCreatingId] = useState("");
  const [error, setError] = useState("");

  const vis = String(defaultVisibility || "PUBLIC").toUpperCase();
  const visInfo = VIS_MAP[vis] || VIS_MAP.PUBLIC;

  const helpText = useMemo(() => {
    if (vis === "PRIVATE")
      return "O quadro será criado como privado, visível somente para você.";
    if (vis === "TEAM")
      return `O quadro será criado no time: ${
        defaultTeamName || "Selecionado"
      }.`;
    return "O quadro será criado como público, visível para todos os usuários.";
  }, [vis, defaultTeamName]);

  // Monta payload compatível com o FunnelManager
  function buildPayloadFromTemplate(tpl) {
    const stages = tpl.stages.map((name, i) => ({
      name,
      order: i + 1,
      probability: 0,
      color: null,
      isWin: i === tpl.closingStageIndex,
      isLost: false,
      autoLeadStatus: null,
      requiredFields: [],
      autoTasks: [],
      slaHours: null,
      wipLimit: null,
      webhookOnEnter: null,
    }));

    return {
      name: tpl.name,
      visibility: vis,
      teamId: vis === "TEAM" ? defaultTeamId : null,
      ownerUserId: vis === "PRIVATE" ? ownerUserId : null,
      closingStageId: null,
      stages,
    };
  }

  const handleSelect = async (tpl) => {
    if (!onCreateFromTemplate) return;
    try {
      setError("");
      setCreatingId(tpl.id);
      const payload = buildPayloadFromTemplate(tpl);
      await onCreateFromTemplate(payload);
      onClose?.();
    } catch (e) {
      setError("Não foi possível criar o funil. Tente novamente.");
    } finally {
      setCreatingId("");
    }
  };

  // Card "Criar do zero"
  const CustomCard = () => (
    <button
      type="button"
      disabled={!!creatingId}
      onClick={() =>
        onCreateCustom?.({
          defaultVisibility: vis,
          defaultTeamId,
          defaultTeamName,
          ownerUserId,
        })
      }
      className={[
        "group relative overflow-hidden rounded-2xl border-2 border-dashed border-gray-300",
        "bg-gray-50 text-left p-4 hover:border-gray-400 hover:bg-gray-100",
        "focus:outline-none focus:ring-2 focus:ring-gray-200",
      ].join(" ")}
    >
      <div className="relative z-10">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-base font-semibold text-gray-900">
              Criar do zero (personalizado)
            </div>
            <div className="mt-1 text-xs text-gray-600">
              Defina o nome do quadro e adicione as etapas uma a uma.
            </div>
          </div>
          <span className="shrink-0 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] text-gray-700">
            flexível
          </span>
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {["Etapa 1", "Etapa 2", "Etapa 3"].map((s, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] bg-white border-gray-200 text-gray-700"
            >
              {s}
            </span>
          ))}
          <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] bg-emerald-50 border-emerald-200 text-emerald-700">
            Fechamento
          </span>
        </div>

        <div className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-gray-800 group-hover:translate-x-0.5 transition-transform">
          Abrir construtor
          <svg width="16" height="16" viewBox="0 0 24 24" className="ml-0.5">
            <path
              fill="currentColor"
              d="m13.172 12l-4.95-4.95l1.414-1.414L16 12l-6.364 6.364l-1.414-1.414z"
            />
          </svg>
        </div>
      </div>
    </button>
  );

  return (
    <Modal show={show} onClose={onClose} size="4xl">
      <Modal.Header>
        <div className="flex items-center gap-2">
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-gray-900 text-white text-xs font-bold">
            F
          </span>
          <div>
            <div className="text-lg font-semibold text-gray-800">{title}</div>
            <div className="text-xs text-gray-500">{subtitle}</div>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        {/* Info de visibilidade “fixa” */}
        <div className="mb-4 flex flex-col md:flex-row md:items-center justify-between">
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <Badge color={visInfo.badge.color} className="capitalize w-fit">
              {visInfo.label}
            </Badge>
            {vis === "TEAM" && (
              <span className="text-xs text-gray-600">
                Time: <b>{defaultTeamName || "—"}</b>
              </span>
            )}
            {vis === "PRIVATE" && (
              <span className="text-xs text-gray-600">
                Dono: <b>{ownerUserId ? `#${ownerUserId}` : "—"}</b>
              </span>
            )}
          </div>
          <div className="text-[11px] text-gray-500">{helpText}</div>
        </div>

        {error && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Cartão "Criar do zero" */}
          <CustomCard />

          {/* Templates prontos */}
          {templates.map((tpl) => {
            const loading = creatingId === tpl.id;
            return (
              <button
                key={tpl.id}
                type="button"
                disabled={!!creatingId}
                onClick={() => handleSelect(tpl)}
                className={[
                  "group relative overflow-hidden rounded-2xl border border-gray-200 bg-white text-left p-4 transition",
                  "hover:shadow-md focus:outline-none focus:ring-2 focus:ring-gray-200",
                  creatingId ? "opacity-60 cursor-wait" : "cursor-pointer",
                ].join(" ")}
              >
                {/* Aura */}
                <div
                  className={`pointer-events-none absolute -top-20 -right-20 h-40 w-40 rounded-full bg-gradient-to-br ${
                    tpl.color || "from-gray-400 to-gray-600"
                  } opacity-20 blur-2xl`}
                />
                <div className="relative z-10">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-base font-semibold text-gray-900">
                        {tpl.name}
                      </div>
                      {!!tpl.description && (
                        <div className="mt-1 text-xs text-gray-600">
                          {tpl.description}
                        </div>
                      )}
                    </div>
                    <span className="shrink-0 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] text-gray-700">
                      {tpl.stages.length} etapas
                    </span>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {tpl.stages.map((s, i) => (
                      <span
                        key={i}
                        className={[
                          "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]",
                          i === tpl.closingStageIndex
                            ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                            : "bg-gray-50 border-gray-200 text-gray-700",
                        ].join(" ")}
                      >
                        {s}
                        {i === tpl.closingStageIndex && (
                          <span className="ml-1 rounded bg-emerald-100 px-1 text-[10px] text-emerald-700">
                            fechamento
                          </span>
                        )}
                      </span>
                    ))}
                  </div>

                  <div className="mt-4 flex items-center justify-between">
                    <div className="text-[11px] text-gray-500">
                      Visibilidade: <b>{visInfo.label}</b>
                      {vis === "TEAM" && defaultTeamName
                        ? ` • ${defaultTeamName}`
                        : ""}
                    </div>
                    <div className="inline-flex items-center gap-2 text-sm font-medium text-gray-800 group-hover:translate-x-0.5 transition-transform">
                      {loading ? "Criando..." : "Usar este template"}
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        className="ml-0.5"
                      >
                        <path
                          fill="currentColor"
                          d="m13.172 12l-4.95-4.95l1.414-1.414L16 12l-6.364 6.364l-1.414-1.414z"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </Modal.Body>

      <Modal.Footer className="flex justify-end">
        <Button color="gray" onClick={onClose} disabled={!!creatingId}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
